import {
  NOTIFICATION_SOCKET_PATH,
  type AdminNotificationView,
  type NotificationSocketClientMessage,
  type NotificationSocketMessage,
} from '@duvamarine/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api';
import { useAdminAuthStore } from '../auth-store';

/* Admin notification centre client: realtime socket and event texts (spec §6.11). */

const BASE_URL = import.meta.env.VITE_API_URL ?? '/api/v1';
const MAX_BACKOFF_MS = 30_000;

/** ws(s):// URL of the notification socket next to the REST API. */
function socketUrl(): string {
  const base = new URL(BASE_URL, window.location.origin);
  base.protocol = base.protocol === 'https:' ? 'wss:' : 'ws:';
  base.pathname = `${base.pathname.replace(/\/$/, '')}${NOTIFICATION_SOCKET_PATH}`;
  return base.toString();
}

/** Localised one-line text of a notification (falls back to the stored Turkish title). */
export function useNotificationText() {
  const { t, i18n } = useTranslation('admin');
  return (item: AdminNotificationView): string => {
    if (!i18n.exists(`admin:notificationCenter.events.${item.event}`)) return item.title;
    // A closed set of placeholders (an index signature would make i18next expect context keys).
    const value = (name: string) => item.data[name] ?? '';
    return t(`notificationCenter.events.${item.event}`, {
      code: value('code'),
      customer: value('customer'),
      listing: value('listing'),
      service: value('service'),
      name: value('name'),
      amount: value('amount'),
      reference: value('reference'),
      rating: value('rating'),
    });
  };
}

/**
 * Live unread counter: one WebSocket per tab, authenticated with the access token in the first
 * message. The server closes it when the token expires (4401); we refresh and reconnect.
 */
export function useNotificationSocket(onNotification: (item: AdminNotificationView) => void) {
  const accessToken = useAdminAuthStore((s) => s.accessToken);
  const queryClient = useQueryClient();
  const [unread, setUnread] = useState<number | null>(null);
  const handler = useRef(onNotification);
  useEffect(() => {
    handler.current = onNotification;
  });

  useEffect(() => {
    if (!accessToken) return undefined;
    let socket: WebSocket | null = null;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let attempts = 0;
    let stopped = false;

    const connect = (token: string) => {
      socket = new WebSocket(socketUrl());
      socket.addEventListener('open', () => {
        const message: NotificationSocketClientMessage = { type: 'auth', token };
        socket?.send(JSON.stringify(message));
      });
      socket.addEventListener('message', (event: MessageEvent<string>) => {
        let message: NotificationSocketMessage;
        try {
          message = JSON.parse(event.data) as NotificationSocketMessage;
        } catch {
          return;
        }
        attempts = 0;
        setUnread(message.unread);
        if (message.type === 'notification') {
          void queryClient.invalidateQueries({ queryKey: ['admin', 'notifications'] });
          handler.current(message.notification);
        } else if (message.type === 'read') {
          void queryClient.invalidateQueries({ queryKey: ['admin', 'notifications'] });
        }
      });
      socket.addEventListener('close', (event) => {
        if (stopped) return;
        attempts += 1;
        const delay = Math.min(MAX_BACKOFF_MS, 1000 * 2 ** Math.min(attempts, 5));
        timer = setTimeout(() => {
          if (stopped) return;
          if (event.code === 4401) {
            // Expired token: a refresh updates the store, which reconnects through this effect.
            void api.refresh();
            return;
          }
          connect(useAdminAuthStore.getState().accessToken ?? token);
        }, delay);
      });
    };

    connect(accessToken);
    return () => {
      stopped = true;
      clearTimeout(timer);
      socket?.close(1000, 'unmount');
    };
  }, [accessToken, queryClient]);

  return unread;
}
